import React from "react";
import { useShow } from "@refinedev/core";
import { Show } from "@refinedev/antd";
import { Card, Descriptions, Tag, Typography, Button, theme } from "antd";
import { DownloadOutlined, FileTextOutlined } from "@ant-design/icons";
import dayjs from 'dayjs';
import { MINUTE_TEMPLATE_ONE_QUERY } from "./queries";
import { IMinuteTemplate } from "./interface";
import { MarkdownEditor } from "../../components/MarkdownEditor";

const { Text, Link } = Typography;
const { useToken } = theme;

export const MinuteTemplateShow: React.FC = () => {
  const { query } = useShow<IMinuteTemplate>({
    resource: "minuteTemplates",
    meta: {
      gqlQuery: MINUTE_TEMPLATE_ONE_QUERY,
    },
  });

  const { token } = useToken();

  const { data, isLoading } = query;
  const record = data?.data;

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!record) {
    return <div>Minute template not found</div>;
  }

  const getContentTypeLabel = (contentType: string) => {
    return contentType.toLowerCase().replace("_", " ");
  };

  const getDocumentTemplateTitle = (contentType: string) => {
    return contentType === "DOCUMENT_TEMPLATE" ? "Document Template" : "Reference Document";
  };

  const getDocumentTemplateDescription = (contentType: string) => {
    if (contentType === "RAW_CONTENT") {
      return "This document template is used as a reference for styling when converting markdown content to formatted documents via pandoc.";
    }
    return null;
  };

  return (
    <Show title="Minute Template Details">
      <Card>
        <Descriptions column={2} layout="vertical" bordered>
          <Descriptions.Item label="Template Name">
            {record.name}
          </Descriptions.Item>
          <Descriptions.Item label="Minute Type">
            {record.minuteType.toLowerCase().replace("_", " ")}
          </Descriptions.Item>
          <Descriptions.Item label="Content Type">
            {getContentTypeLabel(record.contentType)}
          </Descriptions.Item>
          <Descriptions.Item label="Version">
            {record.version}
          </Descriptions.Item>
          <Descriptions.Item label="Venue">
            {record.venue}
          </Descriptions.Item>
          <Descriptions.Item label="Active">
            <Tag color={record.isActive ? "success" : "error"}>
              {record.isActive ? "Active" : "Inactive"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {dayjs(record.createdAt).format('YYYY-MM-DD HH:mm')}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {dayjs(record.updatedAt).format('YYYY-MM-DD HH:mm')}
          </Descriptions.Item>
        </Descriptions>

        {record.contentType === "RAW_CONTENT" && record.content && (
          <Card title="Template Content" style={{ marginTop: 16 }}>
            <MarkdownEditor
              markdown={record.content}
              disabled={true}
              showSaveButton={false}
              height={300}
              enableDiffSource={true}
            />
          </Card>
        )}

        {record.documentTemplateId && record.documentTemplate && (
          <Card 
            title={
              <span>
                <FileTextOutlined style={{ marginRight: 8 }} />
                {getDocumentTemplateTitle(record.contentType)}
              </span>
            } 
            style={{ marginTop: 16 }}
          >
            {getDocumentTemplateDescription(record.contentType) && (
              <div style={{ 
                marginBottom: 16, 
                padding: 12, 
                background: token.colorInfoBg, 
                borderRadius: 6,
                border: `1px solid ${token.colorInfoBorder}`
              }}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {getDocumentTemplateDescription(record.contentType)}
                </Text>
              </div>
            )}
            <Descriptions column={1} layout="vertical" bordered>
              <Descriptions.Item label="Document Name">
                {record.documentTemplate.documentName}
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {record.documentTemplate.category}
              </Descriptions.Item>
              <Descriptions.Item label="Tag">
                {record.documentTemplate.tag}
              </Descriptions.Item>
              <Descriptions.Item label="Template ID">
                {record.documentTemplate.templateId}
              </Descriptions.Item>
              <Descriptions.Item label="Actions">
                <Link href={record.documentTemplate.url} target="_blank" rel="noopener noreferrer">
                  <Button type="primary" icon={<DownloadOutlined />} size="small">
                    View / Download
                  </Button>
                </Link>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {record.documentTemplateId && !record.documentTemplate && (
          <Card 
            title={
              <span>
                <FileTextOutlined style={{ marginRight: 8 }} />
                {getDocumentTemplateTitle(record.contentType)}
              </span>
            } 
            style={{ marginTop: 16 }}
          >
            {getDocumentTemplateDescription(record.contentType) && (
              <div style={{ 
                marginBottom: 16, 
                padding: 12, 
                background: token.colorInfoBg, 
                borderRadius: 6,
                border: `1px solid ${token.colorInfoBorder}`
              }}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {getDocumentTemplateDescription(record.contentType)}
                </Text>
              </div>
            )}
            <Descriptions column={1} layout="vertical" bordered>
              <Descriptions.Item label="Document Template ID">
                {record.documentTemplateId}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}
      </Card>
    </Show>
  );
};
